import type { MetagamesTokenPackageBundles } from '../stores/app/types';
import type { BingoCoinType } from '../stores/bingo/types';
import type { CoinType, ExtendedCoinType } from '../stores/cointype/CoinTypeStore';
import type { GameLabel } from '../stores/game/types';

export enum MetaGamesEventType {
  HAMMERS = 'hammers',
  PRIZE_DROP = 'prizeDrop',
  PUZZLE = 'puzzle',
  TRAIL = 'trail',
  PLINKO = 'plinko',
  BINGO = 'bingo',
  ROULETTE = 'roulette',
}

export type BingoLetter = 'B' | 'I' | 'N' | 'G' | 'O';

export interface MetagamesEventByCoin<G = Record<string, any>> {
  balance: number;
  totalBalance: number;
  gameId: number;
  widgetImageUrl?: string;
  preMessageImageUrl?: string;
  isOptedIn?: boolean;
  isCurrency: boolean;
  currencySymbol: string;
  game?: G;
}

export interface MetagamesUserEvent<G = Record<string, any>> {
  sc?: MetagamesEventByCoin<G>;
  gc?: MetagamesEventByCoin<G>;
  cash?: MetagamesEventByCoin<G>;
  id: string;
  startDate: string;
  endDate: string;
  name: string;
  widgetImageUrl: string;
  gameLabel?: GameLabel;
  typeId: number;
  typeName: MetaGamesEventType;
  flags?: Record<string, any>;
  isCrownSlotOnly?: boolean;
  eventScopes: {
    participatingGames: number[];
    participatingProviders: number[];
  };
}

export interface UserCreditLedger {
  id: string;
  userId: number;
  eventId: string;
  coinType: ExtendedCoinType;
  amount: number;
  source: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface MetagamesResponse {
  metagameEvents: {
    userId: string;
    userEvents: MetagamesUserEvent[];
  };
}

export interface HammerAnimationState {
  hammerAmount: number;
  enable: boolean;
  onAnimationEnd?: VoidFunction;
}

export interface ClaimPuzzleAnimationState {
  puzzleAmount: number;
  enable: boolean;
  coinType?: CoinType;
  onAnimationEnd?: VoidFunction;
}

export interface ClaimDiceAnimationState {
  diceAmount: number;
  enable: boolean;
  coinType?: CoinType;
  onAnimationEnd?: VoidFunction;
}

export interface ClaimBingoAnimationState {
  bingoAmount: number;
  enable: boolean;
  coinType?: BingoCoinType;
  onAnimationEnd?: VoidFunction;
}

export interface MetagamesCurrentEvent<G = Record<string, any>> extends MetagamesUserEvent<G> {
  url?: string;
  token?: string;
  path?: string;
}

export interface MetagameTokenPackageBundle {
  id: string;
  packageId: number;
  bundleType: 'metagameToken';
  bundlePayload: {
    amount: string | number;
    coinType: ExtendedCoinType;
    currencyName: string;
    currencySymbol: string;
    metagameTypeId: number;
  };
}

export interface MetagameTokenPackageData {
  packageId: number;
  name: string;
  amount: number;
  gcCoin: number;
  scCoin: number;
  currency: string;
  isActive: boolean;
  fallbackPackageId: number | null;
  createdAt: string;
  updatedAt: string;
  packageBundles: MetagamesTokenPackageBundles[];
}

export interface MetagamesUpcomingEvent<G = Record<string, any>> {
  id: string;
  name: string;
  isOptedIn: boolean;
  startDate: string;
  endDate: string;
  gameId: number;
  coinType: BingoCoinType;
  balance: number;
  currencySymbol: string;
  totalBalance: number;
  game: G;
}

export interface BingoGameBallCall {
  id: string;
  createdAt: Date;
  callOrder: number;
  ballNumber: number;
  ballNumberBeforeCorrection: number;
}

export interface MetagamesHistoricalEvents<G = Record<string, any>> {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  gameId: number;
  coinType: BingoCoinType;
  currencySymbol: string;
  totalBalance: number;
  game: G;
}

export interface UserMetagameResultsEvent {
  eventId: string;
  gameId: number;
  amountWon: number;
  eventStartDate: Date;
  eventEndDate: Date;
}

export interface UserMetagameEventsResults
  extends Partial<
    Record<
      ExtendedCoinType,
      {
        totalWinnings: number;
        typeId: number;
        typeName: MetaGamesEventType;
        events: UserMetagameResultsEvent[];
      }
    >
  > {}

export interface MetagameTokenPackageResponse {
  data: {
    success: boolean;
    data: MetagameTokenPackageData;
    message: string;
  };
  errors: string[];
}

export interface MetagameTokenPackageRequest {
  quantity: number;
  eventType: MetaGamesEventType;
}

export interface MetagamesOneTimePassResponse {
  data: {
    success: boolean;
    data: MetagamesUpcomingEvent[];
    message: string;
  };
  errors: string[];
}

export interface MetagameResultSocketData {
  metagameName: string;
  gameId: string;
  amounts: {
    GC: number;
    SC: number;
    CASH: number;
  };
  bonusAmounts: {
    GC: number;
    SC: number;
    CASH: number;
  };
}
